/* Cart drawer controller.
 *
 * Lives in an asset rather than inside the section, because the section's own
 * markup is replaced wholesale on every cart change (Section Rendering API) --
 * a <script> inside it would be torn out along with the listeners it registered.
 * Everything here is therefore delegated from document.
 */
(function () {
  'use strict';

  var SECTION = 'cart-drawer';
  var root = function () { return document.querySelector('[data-t3-drawer]'); };
  var lockedY = 0;
  var lastFocus = null;

  /* ------------------------------------------------- add-to-cart attribution */

  /* Which control started the pending add. The sticky bars and the inline
   * button post the SAME form -- the sticky button is merely wired to it from
   * outside via form="..." -- so the form itself carries no trace of which one
   * was pressed. The initiating click is the only moment that distinction
   * exists, so it is recorded here and consumed by the submit handler below.
   */
  var pendingSource = null;

  function track(name, props) {
    if (window.t3track) window.t3track(name, props);
  }

  /* Stamp the originating control onto the form as a hidden line item property.
   *
   * Written into the form as a real input rather than appended to the FormData
   * because there are two ways this form can be sent: the fetch() below, and
   * the native form.submit() fallback that runs if that fetch fails. Only an
   * input in the DOM survives both.
   *
   * The leading underscore is a Shopify convention: it keeps the property out
   * of customer-facing cart and checkout display while still recording it on
   * the order for admin, CSV export and the Admin API. This is what makes the
   * attribution ad-blocker-proof -- it lives in Shopify's own order record, not
   * in a third-party analytics call that can be dropped.
   */
  function applySource(form) {
    var source = pendingSource || 'product-page';
    pendingSource = null;                 // never let one click bleed into the next
    var input = form.querySelector('input[name="properties[_added_via]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_added_via]';
      form.appendChild(input);
    }
    input.value = source;
    return source;
  }

  /* ---------------------------------------------------------------- open/close */

  function open() {
    var el = root();
    if (!el || el.classList.contains('is-open')) return;
    track('cart_drawer_opened');
    lastFocus = document.activeElement;
    lockedY = window.scrollY || window.pageYOffset || 0;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    // Same scroll-lock as the mobile menu: overflow:hidden alone loses the
    // reading position, so pin the body and restore it on close.
    document.body.style.position = 'fixed';
    document.body.style.top = -lockedY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    var close = el.querySelector('.t3drawer__close');
    if (close) requestAnimationFrame(function () { close.focus(); });
  }

  function close() {
    var el = root();
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, lockedY);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ------------------------------------------------------------------ rendering */

  function busy(on) {
    var el = root();
    if (el) el.classList.toggle('is-busy', !!on);
  }

  // Re-render the drawer from Liquid so money formatting stays server-side.
  function refresh() {
    return fetch('/?sections=' + SECTION, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[SECTION];
        if (!html) return;
        var wrap = document.createElement('div');
        wrap.innerHTML = html;
        var fresh = wrap.querySelector('[data-t3-drawer]');
        var current = root();
        if (!fresh || !current) return;
        var wasOpen = current.classList.contains('is-open');
        current.innerHTML = fresh.innerHTML;
        if (wasOpen) {
          current.classList.add('is-open');
          current.setAttribute('aria-hidden', 'false');
        }
      });
  }

  function updateHeaderCount(n) {
    document.querySelectorAll('.t3hdr__cart').forEach(function (cart) {
      var sup = cart.querySelector('.t3hdr__cart-count');
      if (n > 0) {
        if (!sup) {
          sup = document.createElement('sup');
          sup.className = 't3hdr__cart-count';
          cart.appendChild(sup);
        }
        sup.textContent = n;
      } else if (sup) {
        sup.remove();
      }
    });
  }

  function syncCount() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { updateHeaderCount(c.item_count); return c; });
  }

  /* -------------------------------------------------------------------- actions */

  function changeLine(line, qty) {
    busy(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(qty) })
    })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        updateHeaderCount(c.item_count);
        if (cartPage()) { pageOwed = true; maybeRenderCartPage(); }
        return refresh();
      })
      .catch(function () {})
      .then(function () { busy(false); });
  }

  // Used by add-to-cart paths elsewhere in the theme.
  window.t3Cart = {
    open: open,
    close: close,
    refresh: refresh,
    added: function () {
      return syncCount().then(refresh).then(function () {
        var el = root();
        if (el && el.dataset.openOnAdd !== 'false') open();
      });
    }
  };

  /* ------------------------------------------------------------------ listeners */

  document.addEventListener('click', function (e) {
    var el = root();
    if (!el) return;

    // Header cart icon (or the mobile menu's Cart link) -> open the drawer instead of navigating.
    var cartLink = e.target.closest('.t3hdr__cart, [data-t3-cart-open]');
    if (cartLink) {
      e.preventDefault();
      refresh().then(open);
      return;
    }

    if (e.target.closest('[data-t3-drawer-close]')) {
      // The empty-state link is also a close target, but it must still navigate.
      var link = e.target.closest('a[href]');
      if (!link) { e.preventDefault(); close(); }
      return;
    }

    // Closes the on-site funnel: drawer opened -> checkout clicked. Everything
    // past this point happens on Shopify's checkout, which runs no theme JS, so
    // the purchase itself has to come from the Custom Web Pixel instead.
    // Deliberately does NOT preventDefault -- the form must still submit.
    if (e.target.closest('.t3drawer__checkout')) {
      track('checkout_clicked', { from: 'cart_drawer' });
      return;
    }

    var step = e.target.closest('[data-t3-qty]');
    if (step && el.contains(step)) {
      e.preventDefault();
      changeLine(step.getAttribute('data-t3-line-index'), step.getAttribute('data-t3-qty'));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var el = root();
    if (el && el.classList.contains('is-open')) close();
  });

  // Intercept every native add-to-cart form so adding never leaves the page.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
    // Tag BEFORE the drawer check. If the drawer is switched off we hand the
    // POST back to Shopify, and the property still has to ride along on it.
    var source = applySource(form);
    var el = root();
    if (!el) return;                       // drawer disabled -> let Shopify handle it
    e.preventDefault();
    var btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;
    fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); })
      .then(function (item) {
        track('add_to_cart', {
          source: source,                                   // 'sticky-bar' | 'product-page'
          product: item && item.product_title,
          variant_id: item && item.variant_id,
          price: item && typeof item.price === 'number' ? item.price / 100 : undefined
        });
        return window.t3Cart.added();
      })
      .catch(function () { form.submit(); })   // fall back to the normal POST
      .then(function () { if (btn) btn.disabled = false; });
  });

  /* --------------------------------------------------------------- cart upsell */

  /* Buttons rendered by snippets/cart-upsell, in the drawer and on /cart.
   * Delegated separately from the drawer listener above, because /cart has to
   * work even when the drawer is switched off.
   *
   *   add     quick-add an offer to a cart with no rum in it
   *   switch  replace every rum line with one bigger pack
   *
   * Both re-read /cart.js first and give up if the cart no longer matches what
   * was rendered (another tab, or the drawer and /cart disagreeing), because a
   * stale offer could push the order past 6 bottles, which ships at $19.99.
   *
   * A switch removes first and adds second. Adding first looks safer, but in
   * quantity mode the new line can be the SAME variant with the same properties
   * as a line being removed, Shopify merges them, and removing the old key would
   * then empty it. If the remove went through and the add did not, the removed
   * lines are put back.
   */
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); });
  }

  function addOffer(btn) {
    return postJSON('/cart/add.js', { items: [{
      id: Number(btn.dataset.vid),
      quantity: Number(btn.dataset.qty) || 1,
      properties: { _added_via: 'cart-upsell' }
    }] });
  }

  var STALE = { description: 'Your cart changed. Here it is up to date.', stale: true };

  // Rum lines in the live cart, and how many bottles they hold.
  function rumIn(cart, map) {
    var lines = cart.items.filter(function (i) { return map[i.product_id]; });
    var bottles = lines.reduce(function (n, i) { return n + i.quantity * map[i.product_id]; }, 0);
    return { lines: lines, bottles: bottles };
  }

  function runOffer(btn, box) {
    var map = {};
    try { map = JSON.parse(box.dataset.rum || '{}'); } catch (e) {}
    var expected = Number(box.dataset.bottles) || 0;
    var removed = null;                    // set once the remove has gone through

    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var rum = rumIn(cart, map);
        if (rum.bottles !== expected) throw STALE;
        if (btn.dataset.t3Upsell !== 'switch' || !rum.lines.length) return addOffer(btn);

        var updates = {};
        rum.lines.forEach(function (i) { updates[i.key] = 0; });
        return postJSON('/cart/update.js', { updates: updates })
          .then(function () {
            removed = rum.lines;
            return addOffer(btn);
          })
          .catch(function (err) {
            if (!removed) throw err;         // nothing was removed, nothing to put back
            return postJSON('/cart/add.js', { items: removed.map(function (i) {
              return { id: i.variant_id, quantity: i.quantity, properties: i.properties || {} };
            }) }).then(function () { throw err; }, function () { throw err; });
          });
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-t3-upsell]');
    if (!btn || btn.disabled) return;
    var box = btn.closest('.t3up');
    if (!box) return;
    e.preventDefault();
    var mode = btn.dataset.t3Upsell;
    var onCartPage = box.classList.contains('t3up--page');
    // Any cart change from here on can make the OTHER copy (drawer vs. page) stale.
    var onCart = !!document.querySelector('.t3cart');
    if (onCart) pageFrozen = true;

    document.querySelectorAll('[data-t3-upsell]').forEach(function (b) { b.disabled = true; });
    var old = box.querySelector('.t3up__err');
    if (old) old.remove();
    if (!onCartPage) busy(true);

    runOffer(btn, box).then(function () {
      track(mode === 'switch' ? 'cart_upsell_switch' : 'cart_upsell_add', {
        offer: btn.dataset.offer,
        from: onCartPage ? 'cart_page' : 'cart_drawer'
      });
      if (onCart) { window.location.reload(); return; }
      // The cart has changed. If the re-render fails, reload rather than leave
      // buttons up that describe the old cart.
      return syncCount().then(refresh).then(function () {
        var close = document.querySelector('.t3drawer__close');
        if (close) close.focus();
      }, function () { window.location.reload(); });
    }, function (err) {
      // On a failed switch the cart may still have changed, and STALE means it
      // already has; either way only a fresh render can be trusted.
      if (onCart && (err === STALE || mode === 'switch')) { window.location.reload(); return; }
      var msg = (err && err.description) || 'Something went wrong. Please try again.';
      var render = onCartPage ? Promise.resolve() : syncCount().then(refresh);
      return render.then(function () {
        var target = onCartPage ? box : document.querySelector('.t3drawer [data-t3-drawer-msg]');
        if (!target) return;
        document.querySelectorAll('[data-t3-upsell]').forEach(function (b) { b.disabled = false; });
        pageFrozen = false;
        var p = document.createElement('p');
        p.className = 't3up__err font-merriweather';
        p.setAttribute('role', 'alert');
        p.textContent = msg;
        target.appendChild(p);
      }, function () { window.location.reload(); });
    }).then(function () { if (!onCartPage) busy(false); });
  });

  // The sticky buy bars submit their form via form="…" from outside the form.
  document.addEventListener('click', function (e) {
    var b = e.target.closest('button[form][type="submit"]');
    if (!b) return;
    var form = document.getElementById(b.getAttribute('form'));
    if (form && form.matches('form[action*="/cart/add"]')) {
      e.preventDefault();
      // A submit button wired to a form it sits OUTSIDE of is unique to the
      // sticky bars (product.liquid and product-hero.liquid); the inline button
      // lives inside its form. That structural difference is the whole basis of
      // the attribution.
      pendingSource = 'sticky-bar';
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  });

  /* ------------------------------------------------------------ /cart page */

  /* The /cart page applies quantity changes as they happen, like the drawer:
   * each change goes to /cart/change.js (by line key) and the section is then
   * re-rendered from Liquid, so line prices, the shipping note, the nudge, the
   * bundle cards and the subtotal always describe the cart as it is. Without JS
   * the form still posts normally, which is why the "Update cart" button stays
   * in the markup and is only hidden (t3cart--live).
   *
   * Changes run one at a time (pageQueue). A re-render is "owed" after every
   * change and runs only once nothing is in flight and no typing is waiting
   * on its debounce, so a burst of edits renders once, at the end.
   */
  var pageQueue = Promise.resolve();
  var pageTimers = {};      // line key -> debounce timer
  var pageSent = {};        // line key -> last quantity sent since the last render
  var pageInflight = 0;
  var pageOwed = false;
  var pageErrors = [];
  var pageRenderSeq = 0;
  var pageFrozen = false;   // an upsell switch is running; it reloads the page itself

  function cartPage() { return document.querySelector('[data-t3-cart-page]'); }
  function pageTyping() { return Object.keys(pageTimers).length > 0; }

  function setPageBusy(on) {
    var sec = cartPage();
    if (!sec) return;
    sec.classList.toggle('is-busy', !!on);
    if (on) sec.setAttribute('aria-busy', 'true'); else sec.removeAttribute('aria-busy');
  }

  /* Swap in the fresh markup without destroying the quantity box the shopper is
   * in: every node around it is replaced, the box itself only takes the new
   * value. Removing a focused input would drop focus and, on phones, close the
   * keyboard mid-edit. Falls back to a plain swap if the structures differ.
   */
  function swapKeeping(current, fresh, keep, keepFresh) {
    var a = [keep], b = [keepFresh];
    while (a[0] !== current && b[0] !== fresh) {
      if (!a[0].parentNode || !b[0].parentNode) break;
      a.unshift(a[0].parentNode);
      b.unshift(b[0].parentNode);
    }
    if (a[0] !== current || b[0] !== fresh) { current.innerHTML = fresh.innerHTML; return false; }
    for (var i = 0; i < a.length - 1; i++) {
      var A = a[i], B = b[i], aKeep = a[i + 1], bKeep = b[i + 1];
      Array.prototype.slice.call(A.childNodes).forEach(function (n) { if (n !== aKeep) A.removeChild(n); });
      var before = true;
      Array.prototype.slice.call(B.childNodes).forEach(function (n) {
        if (n === bKeep) { before = false; return; }
        if (before) A.insertBefore(n, aKeep); else A.appendChild(n);
      });
      if (aKeep !== keep) {
        // Carry the fresh wrapper's attributes (classes etc.) onto the kept one.
        Array.prototype.slice.call(bKeep.attributes).forEach(function (at) { aKeep.setAttribute(at.name, at.value); });
      }
    }
    keep.defaultValue = keepFresh.defaultValue;
    keep.value = keepFresh.value;
    return true;
  }

  function maybeRenderCartPage(force) {
    var sec = cartPage();
    if (!sec || pageFrozen) return;
    if (pageInflight > 0 || pageTyping()) return;      // whoever finishes last renders
    if (!pageOwed && !force) { setPageBusy(false); return; }
    pageOwed = false;
    var mine = ++pageRenderSeq;
    var id = sec.dataset.sectionId;
    // No JSON Accept header: on /cart that returns the cart itself instead of the section.
    var url = window.location.pathname + '?sections=' + encodeURIComponent(id);
    var get = function () {
      return fetch(url).then(function (r) { if (!r.ok) throw new Error('render ' + r.status); return r.json(); });
    };
    // One retry: a single failed render shouldn't cost the shopper a full reload.
    get().catch(get).then(function (data) {
      // Something newer started while this was in flight: it will render.
      if (mine !== pageRenderSeq || pageInflight > 0 || pageTyping() || pageFrozen) { pageOwed = true; return; }
      var active = document.activeElement;
      var focusInput = active && active.matches && active.matches('.t3cart__qty-input') && sec.contains(active) ? active : null;
      // An emptied box is mid-edit; wait for the next keystroke or for it to lose focus.
      if (focusInput && focusInput.value === '') { pageOwed = true; setPageBusy(false); return; }
      var html = data && data[id];
      var wrap = document.createElement('div');
      wrap.innerHTML = html || '';
      var fresh = wrap.querySelector('[data-t3-cart-page] .t3cart__inner');
      var current = sec.querySelector('.t3cart__inner');
      if (!fresh || !current) throw new Error('render');
      var focusRemove = active && active.matches && active.matches('.t3cart__remove') && sec.contains(active);
      var freshInput = focusInput && fresh.querySelector('.t3cart__qty-input[data-line-key="' + focusInput.dataset.lineKey + '"]');
      if (freshInput) swapKeeping(current, fresh, focusInput, freshInput);
      else current.innerHTML = fresh.innerHTML;
      pageSent = {};
      if (focusRemove || (focusInput && !freshInput)) {
        // The focused control went away with its line: land on the next box, or the heading.
        var next = current.querySelector('.t3cart__qty-input') || sec.querySelector('.t3cart__heading');
        if (next) { if (!next.matches('input')) next.setAttribute('tabindex', '-1'); next.focus(); }
      }
      if (pageErrors.length) {
        var anchor = current.querySelector('.t3cart__list') || current.querySelector('.t3cart__heading') || current.firstElementChild;
        var p = document.createElement('p');
        p.className = 't3cart__err font-merriweather';
        p.setAttribute('role', 'alert');
        p.textContent = pageErrors[pageErrors.length - 1];
        if (anchor) anchor.insertAdjacentElement('afterend', p); else current.appendChild(p);
        pageErrors = [];
      }
      setPageBusy(false);
    }).catch(function () {
      // A failed render can't be trusted to describe the cart; reload instead.
      window.location.reload();
    });
  }

  function changeCartPageLine(key, qty) {
    if (!cartPage()) return;
    pageSent[key] = qty;
    pageInflight++;
    setPageBusy(true);
    pageQueue = pageQueue.then(function () {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.json().then(function (c) { if (!r.ok) throw c; return c; }); })
        .then(function (c) {
          updateHeaderCount(c.item_count);
          track('cart_page_qty', { quantity: qty });
        }, function (err) {
          // e.g. more than are in stock: the cart keeps what it had, and the
          // re-render shows that quantity again, with this message.
          pageErrors.push((err && (err.description || err.message)) || 'Something went wrong. Please try again.');
        })
        .then(function () {
          pageInflight--;
          pageOwed = true;
          maybeRenderCartPage();
        });
    });
  }

  function queueCartPageInput(input, delay) {
    var key = input.dataset.lineKey;
    if (!key) return;
    if (input.value === '') {
      // Left empty: put the real quantity back. While still typing, just wait.
      if (!delay) {
        clearTimeout(pageTimers[key]);
        delete pageTimers[key];
        maybeRenderCartPage(true);
      }
      return;
    }
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) return;
    clearTimeout(pageTimers[key]);
    delete pageTimers[key];
    var last = key in pageSent ? pageSent[key] : parseInt(input.defaultValue, 10);
    if (qty === last) { maybeRenderCartPage(); return; }   // a render held back for this box may be owed
    if (!delay) { changeCartPageLine(key, qty); return; }
    pageTimers[key] = setTimeout(function () {
      delete pageTimers[key];
      changeCartPageLine(key, qty);
    }, delay);
  }

  // Flush any typing still waiting on its debounce (used before checkout).
  function flushCartPage() {
    Object.keys(pageTimers).forEach(function (key) {
      clearTimeout(pageTimers[key]);
      delete pageTimers[key];
      var input = document.querySelector('[data-t3-cart-page] .t3cart__qty-input[data-line-key="' + key + '"]');
      var qty = input ? parseInt(input.value, 10) : NaN;
      if (!isNaN(qty) && qty >= 0) changeCartPageLine(key, qty);
    });
    return pageQueue;
  }

  (function () {
    var sec = cartPage();
    if (!sec) return;
    sec.classList.add('t3cart--live');
    // Typing waits for a pause; the spinner arrows, Enter and leaving the box apply at once.
    sec.addEventListener('input', function (e) {
      if (e.target.matches('.t3cart__qty-input')) queueCartPageInput(e.target, 600);
    });
    sec.addEventListener('change', function (e) {
      if (e.target.matches('.t3cart__qty-input')) queueCartPageInput(e.target, 0);
    });
    sec.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('.t3cart__qty-input')) {
        e.preventDefault();
        queueCartPageInput(e.target, 0);
      }
    });
    sec.addEventListener('click', function (e) {
      var rm = e.target.closest('.t3cart__remove');
      if (rm) {
        var row = rm.closest('.t3cart__row');
        var input = row && row.querySelector('.t3cart__qty-input[data-line-key]');
        if (!input) return;                // fall back to the link's own /cart/change URL
        e.preventDefault();
        var key = input.dataset.lineKey;
        if (pageSent[key] === 0) return;   // already being removed
        clearTimeout(pageTimers[key]);
        delete pageTimers[key];
        changeCartPageLine(key, 0);
        return;
      }
      // Checkout with changes still pending: let them land first, then submit for real.
      var co = e.target.closest('.t3cart__checkout');
      if (co && (pageTyping() || pageInflight > 0)) {
        e.preventDefault();
        co.disabled = true;
        flushCartPage().then(function () {
          var form = co.form;
          if (!form) { window.location.href = '/checkout'; return; }
          var hidden = document.createElement('input');
          hidden.type = 'hidden'; hidden.name = 'checkout'; hidden.value = '';
          form.appendChild(hidden);
          form.submit();
        });
      }
    });
  })();

  /* ------------------------------------------------------ checkout switch links */

  /* The "Switch to 3 bottles" buttons in checkout (Checkout Blocks) link to
   * /?view=switch&t3switch=<variant id>:<qty>, a bare page
   * (templates/index.switch.liquid) that replaces the cart with that offer
   * and goes straight back to checkout.
   *
   * A plain cart permalink (/cart/<id>:<qty>) can't do it: links clicked inside
   * checkout get the checkout's own _r session token appended, and the
   * permalink then resumes that same checkout, unchanged.
   */
  var sw = window.location.search.match(/[?&]t3switch=(\d+):(\d{1,2})(?:&|$)/);
  // Links without view=switch land on the full page; hand them to the bare
  // switch page (templates/index.switch.liquid), which does the work safely.
  if (sw && !/[?&]view=switch(?:&|$)/.test(window.location.search)) {
    window.location.replace('/?view=switch&t3switch=' + sw[1] + ':' + sw[2]);
  }
})();
